<script>
import { onMount, onDestroy } from "svelte";
import { uiState } from "@app/store/uiStore.svelte.js";
import { environment } from "@app/store/envStore.svelte.js";
import {
    modalState,
    showModal,
    hideModal,
} from "@app/store/modalStore.svelte.js";
import { styleState } from "@app/store/styleStore.svelte.js";
import { externalDataState } from "@app/store/externalDataStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { fade } from "svelte/transition";
import { projectState } from "@app/store/projectStore.svelte.js";
import { updateDuration } from "@app/store/playerStore.svelte.js";
import {
    markerState,
    resetMarkerState,
    resetMarkerStateWithData,
} from "@app/store/markerStore.svelte.js";
import { issueState } from "@app/store/issueStore.svelte.js";
import { speakerState } from "@app/store/speakerStore.svelte.js";
import { metadataState } from "@app/store/metadataStore.svelte.js";
import {
    eventGroupState,
    resetEventGroupState,
} from "@app/store/eventGroupStore.svelte.js";
import {
    insertHistoryAction,
    resetHistory,
} from "@app/store/historyStore.svelte.js";
import { authState } from "@app/store/authStore.svelte.js";
import * as m from "@app/paraglide/messages.js";

import _Event from "@cclib/classes/event.js";
import _EventGroup from "@cclib/classes/eventGroup.js";
import _Project from "@cclib/classes/project.js";
import _Marker from "@cclib/classes/marker.js";
import loadMedia from "@cclib/functions/projects/loadMedia.js";
import decode from "@cclib/functions/decode.js";
import encode from "@cclib/functions/encode.js";
import defaults from "@cclib/lib/defaults.js";
import encodings from "@cclib/dict/encodings.js";
import formats from "@cclib/lib/formats.js";
import orderByTime from "@cclib/functions/eventGroups/orderByTime.js";
import removeHtmlEntities from "@cclib/functions/eventGroups/removeHtmlEntities.js";
import wrapStyledTextWithSpan from "@cclib/functions/quill/wrapStyledTextWithSpan.js";
import frameRates from "@cclib/dict/frameRates.js";
import supportedLanguages from "@cclib/dict/supportedLanguages.js";
import dropFrameCv from "@cclib/functions/special/dropFrameConvert.js";
import bufferToHex from "@cclib/functions/utility/bufferToHex.js";
import fixOverlap from "@cclib/functions/eventGroups/fixOverlap.js";
import checkForOverlaps from "@cclib/functions/eventGroups/checkForOverlaps.js";
import automaticOs from "@cclib/functions/special/automaticOffset.js";
import getAutomaticOffsetAmount from "@cclib/functions/special/getAutomaticOffsetAmount.js";
import stripTags from "@cclib/functions/quill/stripTags.js";
import encodeHtml from "@cclib/functions/eventGroups/encodeHtml.js";
import replaceTrialText from "@cclib/functions/eventGroups/replaceTrialText.js";
import tcLib from "@cclib/lib/timecode.js";
import insertEvent from "@cclib/functions/eventGroups/insertEvent.js";
import { ccprjFunc } from "@app/lib/ccprj.js";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { saveAs } from "file-saver";
import PP from "@app/external/penpal/penpal-v4.js";
let Penpal = PP();
let connection = null;
let frameRate;
let exporting = false;
let progress = $state(0),
    statusMsg = $state(m.ateliere_connect_status_initializing()),
    modalTitle = $state(m.ateliere_connect_title_project_import());

onMount(async () => {
    try {
        updateStatus({
            progress: 5,
            msg: m.ateliere_connect_status_connecting(),
        });

        window.addEventListener(
            "handleAtelierePublishEvent",
            exportCaptionFile,
        );

        console.log("[Step 1] Connecting to Ateliere parent using Penpal v4");
        connection = await Penpal.connectToParent({
            // Debug can be helpful during development
            debug: false,
        }).promise;

        console.log("Connected to Ateliere parent:", connection);

        updateStatus({
            progress: 15,
            msg: m.ateliere_connect_status_getting_file_context(),
        });

        console.log(
            "[Step 2] Getting context from Ateliere and getting caption/video file IDs",
        );
        const { query, mutate, getContext, getValidSession } = connection;
        const { id, idToken, page, slug, url, pathname, captionId, videoId } =
            await getContext();

        externalDataState.idToken = idToken;
        externalDataState.slug = slug;

        console.log("Caption File ID:", captionId);
        console.log("Video File ID:", videoId);

        let videoFileDetailsRes = await getVideoFileDetails(videoId, query);
        let captionFileDetailsRes = await getCaptionFileDetails(
            captionId,
            query,
        );

        console.log("Video File Details:", videoFileDetailsRes);
        console.log("captionFileDetailsRes Details:", captionFileDetailsRes);

        let captionFileName = captionFileDetailsRes.file.filename;
        let captionFileExtension = captionFileDetailsRes.file.extension;
        let captionFileBody = captionFileDetailsRes.file.body;
        externalDataState.titleId = captionFileDetailsRes.file.titleId;
        // let captionFileUrl = captionFileDetailsRes.file.downloadUrl; // Not used currently
        let avgFrameRate =
            videoFileDetailsRes.file.tracks[0].properties.probeResult
                .avgFrameRate;
        frameRate = getFrameRateFromString(avgFrameRate);

        await importCaptionFile(
            captionFileName,
            captionFileExtension,
            captionFileBody,
        );
        await importVideoFile(videoId);
    } catch (err) {
        console.error(err);
        toast.push(m.ateliere_connect_import_failed({ message: err.message }), {
            classes: ["toast-danger"],
        });

        /* Alert the user using Sweet Alert Library */
        Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.ok,
            icon: "error",
            title: m.ateliere_connect_error_title(),
            text: m.ateliere_connect_import_failed({ message: err.message }),
            confirmButtonText: m.ateliere_connect_ok_button(),
        });

        hideModal();
    } finally {
        updateStatus({
            progress: 100,
            msg: m.ateliere_connect_status_import_complete(),
        });

        // Close the modal after a short delay to show completion
        setTimeout(() => {
            toast.push(`Project Import Complete.`, {
                classes: ["toast-success"],
            });
            hideModal();
        }, 1000);
    }
});

async function getCaptionFileDetails(captionId, query) {
    updateStatus({
        progress: 30,
        msg: m.ateliere_connect_status_caption_details({ id: captionId }),
    });

    // Get file details using GraphQL query
    const captionFileDetailsRes = await query(
        `query GetFileDetails($fileId: ID!) {
            file(id: $fileId) {
                id,      
                titleId,
                info,
                filename,
                extension,
                body,
                type,
                previewUrl,
                downloadUrl
            }
        }`,
        { fileId: captionId },
    );

    return captionFileDetailsRes;
}

async function getVideoFileDetails(videoId, query) {
    updateStatus({
        progress: 35,
        msg: m.ateliere_connect_status_video_details({ id: videoId }),
    });

    const videoFileDetailsRes = await query(
        `query GetFileDetails($fileId: ID!) {
            file(id: $fileId) {
                id
                filename
                type
                previewUrl,
                tracks {
                    id
                    properties {
                        probeResult
                    }
                }
                thumbnails{medium{width height signedUrl}},
            }
        }`,
        { fileId: videoId },
    );

    return videoFileDetailsRes;
}

async function importCaptionFile(fileName, fileExt, fileContents) {
    let profile = getSubtitleFormat(fileExt);
    externalDataState.source_profile = profile;
    console.log("Decoding caption file:", fileName, "with profile:", profile);
    updateStatus({
        progress: 40,
        msg: m.ateliere_connect_status_importing_caption({ name: fileName }),
    });

    let eventGroupOptions = {
        type: "subtitle",
        name: fileName,
        maxLines: 2,
        maxChars: 32,
        minDuration: 0.5,
        maxDuration: 12,
        overlap: true,
    };

    eventGroupState.push({ ...new _EventGroup(eventGroupOptions) });
    projectState.selected = eventGroupState.length - 1;

    /* Translate for Creator */
    let options = new defaults.options({
        profile: profile,
        formatOptions: [],
        frameRate: projectState.frameRate,
        dropFrame: projectState.dropFrame,
        target_profile: "closedCaptionProject",
        source_profile: profile,
        incode: tcLib.secToTc(
            projectState.incode,
            projectState.frameRate,
            projectState.dropFrame,
        ),
        window: {
            width: styleState.width,
            height: styleState.height,
            xPadding: styleState.xPadding,
            yPadding: styleState.yPadding,
        },
    });

    let decodedEvGroup = await decode(fileContents, options);
    decodedEvGroup = encodeHtml(decodedEvGroup);
    eventGroupState[projectState.selected].events = decodedEvGroup.events;
}

async function importVideoFile(videoId) {
    console.log("Importing video file with ID:", videoId);
    updateStatus({
        progress: 70,
        msg: m.ateliere_connect_status_importing_video(),
    });

    const hlsStreamingUrl = `https://mediaview.demo.ateliere.com/media-playlist?fileId=${videoId}`;

    console.log("HLS Streaming URL:", hlsStreamingUrl);
    player.src = { src: hlsStreamingUrl, type: "application/x-mpegurl" };
}

async function exportCaptionFile() {
    if (exporting) {
        console.log("Already exporting, ignoring event.");
        return;
    }

    try {
        exporting = true;
        modalTitle = m.ateliere_connect_title_project_export();
        updateStatus({
            progress: 5,
            msg: m.ateliere_connect_status_starting_export(),
        });

        /* Export Caption file to Ateliere Connect.  */
        let fileBlob;
        let copyOfEventGroup = JSON.parse(
            JSON.stringify(eventGroupState[projectState.selected]),
        );

        updateStatus({
            progress: 25,
            msg: m.ateliere_connect_status_processing_event_group(),
        });

        copyOfEventGroup = orderByTime(copyOfEventGroup);
        copyOfEventGroup = removeHtmlEntities(copyOfEventGroup);
        copyOfEventGroup.events.forEach((event, index, events) => {
            events[index].text = wrapStyledTextWithSpan(event.text);
        });

        updateStatus({
            progress: 55,
            msg: m.ateliere_connect_status_events_processed({ count: copyOfEventGroup.events.length }),
        });

        let options = new defaults.options({
            profile:
                formats.profileMapping["SubRip Video Subtitle Script"].name,
            formatOptions:
                formats.profileMapping["SubRip Video Subtitle Script"].options
                    .encode,
            frameRate: 25,
            dropFrame: false,
            target_profile:
                formats.profileMapping["SubRip Video Subtitle Script"].name,
            source_profile: "closedCaptionProject",
            incode: "00:00:00:00",
            window: {
                width: styleState.width,
                height: styleState.height,
                xPadding: styleState.xPadding,
                yPadding: styleState.yPadding,
            },
        });

        let defaultEncoding =
            formats.defaultEncodingMap[
                formats.profileMapping["SubRip Video Subtitle Script"].name
            ];
        let output = await encode(copyOfEventGroup, options);

        updateStatus({
            progress: 65,
            msg: m.ateliere_connect_status_encoding_complete(),
        });

        if (defaultEncoding === "buffer") {
            if (trgExt === "xlsx") {
                fileBlob = new Blob([output], {
                    type: "application/octet-stream",
                });
            } else {
                fileBlob = output;
            }
        } else if (defaultEncoding.toLowerCase() === "hex") {
            let byteArray = new Uint8Array(
                output.match(/.{2}/g).map((e) => parseInt(e, 16)),
            );
            fileBlob = new Blob([byteArray], {
                type: "application/octet-stream",
            });
        } else if (defaultEncoding.toLowerCase() === "utf-16le") {
            fileBlob = new Blob([output], {
                type: "text/plain;charset=utf-16le",
            });
        } else {
            fileBlob = new Blob([output], {
                type: "text/plain;charset=utf-8",
            });
        }

        updateStatus({
            progress: 75,
            msg: m.ateliere_connect_status_uploading_to_connect(),
        });

        const { mutate } = connection;
        // Step 1: Start the upload and get the presigned URL
        let swalResCustomName = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.confirmCancel,
            title: m.ateliere_connect_caption_name_title(),
            input: 'text',
            inputLabel: m.ateliere_connect_caption_name_label(),
            inputValue: copyOfEventGroup.name
                ? copyOfEventGroup.name.replace(/\.[^/.]+$/, "")
                : m.ateliere_connect_caption_name_default(),
            showCancelButton: true,
            confirmButtonText: m.ateliere_connect_publish_button(),
            cancelButtonText: m.ateliere_connect_cancel_button(),
            preConfirm: (value) => {
                if (!value) {
                    Swal.showValidationMessage(m.ateliere_connect_caption_name_required());
                }
                return value;
            }
        });

        if (swalResCustomName.isDismissed) {
            throw new Error(m.ateliere_connect_export_cancelled());
        }

        const customFileName = await swalResCustomName.value;

        const uploadRes = await mutate(
            `mutation StartUpload($name: String!, $language: String, $type: String!, $uploadLocation: String, $titleId: ID, $allowReplace: Boolean) {
                startUpload(
                    name: $name
                    language: $language
                    type: $type
                    uploadLocation: $uploadLocation
                    titleId: $titleId
                    allowReplace: $allowReplace
                ) {
                    fileId
                    url
                }
            }`,
            {
                name: `${customFileName || m.ateliere_connect_caption_name_default()}.srt`, // Use actual filename
                language: projectState.language || "eng", // Use the project's language
                type: "text/srt",
                titleId: externalDataState.titleId, // Use the titleId from import
                allowReplace: true, // Set to true to replace existing file
                uploadLocation: "/demo/connect/uploads" // or "project" depending on where you want to store it
                //demo/connect/uploads
            },
        );

        console.log("Upload Response:", uploadRes);

        updateStatus({
            progress: 85,
            msg: m.ateliere_connect_status_uploading_file(),
        });

        // Step 2: Upload the actual file to the presigned URL
        const uploadResponse = await fetch(uploadRes.startUpload.url, {
            method: 'PUT',
            body: fileBlob,
            headers: {
                'Content-Type': 'text/plain;charset=utf-8',
            },
        });

        console.log(uploadResponse);

        updateStatus({
            progress: 100,
            msg: m.ateliere_connect_status_upload_complete(),
        });

        toast.push(m.ateliere_connect_caption_published_success(), {
            classes: ["toast-success"],
        });

        // Close modal after short delay
        setTimeout(async () => {
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                icon: "success",
                title: m.ateliere_connect_export_complete_title(),
                text: m.ateliere_connect_export_complete_text(),
                confirmButtonText: m.ateliere_connect_ok_button(),
            });
            // Navigate the user to the files page of their organization
            const filesUrl = `/files`;
            console.log('Navigating to:', filesUrl);
            
            try {
                // Use Penpal connection to navigate in the parent window
                if (connection && typeof connection.navigate === 'function') {
                    await connection.navigate(filesUrl);
                } else {
                    // Fallback: try using window.top to navigate the parent
                    window.top.location.href = `${window.location.origin}${filesUrl}`;
                }
            } catch (error) {
                console.error('Navigation error:', error);
            }
        }, 1500);
    } catch (err) {
        console.log(err, err.message);
    } finally {
        exporting = false;
    }
}

// Helper function to determine subtitle format from filename
function getSubtitleFormat(extension) {
    const formatMap = {
        srt: "subRip",
        vtt: "webVtt",
        ttml: "ttml",
        xml: "ttml", // Assuming XML is TTML
        itt: "iTunesTimedText",
        stl: "ebuStl",
        scc: "scenerist",
    };

    return formatMap[extension] || null;
}

function getFrameRateFromString(frameRateStr) {
    let frameRateMapping = {
        "23.976/1": 23.976,
        "24/1": 24,
        "24": 24,
        "25": 25,
        "25/1": 25,
        "30/1": 30,
        "30": 30,
        "29.97": 29.97,
        "30000/1001": 29.97,
        "24000/1001": 23.976,
        "50": 50,
        "50/1": 50,
        "60": 60,
        "60/1": 60,
        "60000/1001": 59.94,
    };
    return frameRateMapping[frameRateStr] || 25;
}

async function updateStatus(statusInfo) {
    progress = statusInfo.progress;
    statusMsg = statusInfo.msg;
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'ateliereConnectRemote'
        ? 'show d-block'
        : ''}"
    role="dialog"
    tabindex="-1"
    id="AteliereConnectRemoteModal"
    aria-labelledby="ateliereConnectRemoteTitle"
    aria-describedby="ateliereConnectRemoteDesc">
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 id="ateliereConnectRemoteTitle" class="modal-title">
                    <img
                        id="systemLogoImg"
                        alt={m.system_logo_alt()}
                        class="me-1"
                        src="./assets/img/logos/{environment.system}-icon.png" />
                    {modalTitle}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label={m.ateliere_connect_close_label()}
                    onclick={hideModal}
                    id="closeIFrameRemote"></button>
            </div>
            <div class="modal-body" id="iFrameRemoteDesc">
                <p>
                    <i class="bi bi-exclamation-diamond-fill"></i>
                    {statusMsg}
                </p>
                <div class="progress">
                    <div
                        class="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        style="width: {progress}%;"
                        aria-valuenow={progress}
                        aria-valuemin="0"
                        aria-valuemax="100">
                        {progress}%
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

<style lang="css">
#systemLogoImg {
    height: 2rem;
    border-radius: 50%;
}
</style>
